import { useState, useMemo, useRef, useEffect } from 'react';

const LOCALSTORAGE_KEY = 'docFilter_selectedProviders';

export function useProviderFilter(patients) {
  const [selectedProviders, setSelectedProviders] = useState(() => {
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [providerDropdownOpen, setProviderDropdownOpen] = useState(false);
  const providerDropdownRef = useRef(null);

  // Derive unique providers from patient data
  const uniqueProviders = useMemo(() => {
    const providerSet = new Set();
    patients.forEach(p => {
      if (p.provider && p.provider.trim()) {
        providerSet.add(p.provider.trim());
      }
    });
    return [...providerSet].sort((a, b) => a.localeCompare(b));
  }, [patients]);

  // Remove stale providers that no longer exist in the data
  useEffect(() => {
    if (uniqueProviders.length > 0 && selectedProviders.length > 0) {
      const valid = selectedProviders.filter(p => uniqueProviders.includes(p));
      if (valid.length !== selectedProviders.length) {
        setSelectedProviders(valid);
      }
    }
  }, [uniqueProviders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist provider selection to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(selectedProviders));
    } catch {
      // ignore storage errors
    }
  }, [selectedProviders]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (providerDropdownRef.current && !providerDropdownRef.current.contains(e.target)) {
        setProviderDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleProvider = (provider) => {
    setSelectedProviders(prev =>
      prev.includes(provider)
        ? prev.filter(p => p !== provider)
        : [...prev, provider]
    );
  };

  const clearProviderFilter = () => {
    setSelectedProviders([]);
  };

  return {
    selectedProviders,
    setSelectedProviders,
    uniqueProviders,
    providerDropdownOpen,
    setProviderDropdownOpen,
    providerDropdownRef,
    toggleProvider,
    clearProviderFilter,
  };
}
